import React, { useState, useEffect } from 'react';
import {
  useNavigate,
  useLocation,
  Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackSignUp,
  trackUserTypeSelection } from '../services/analytics';
import { catalogoAPI,
  instrumentosAPI } from '../services/api';
import { getMiEntidadNombreDefecto } from '../utils/miEntidadNombreDefecto';
import SEO from './SEO.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Checkbox,
  NativeSelect,
  Spinner,
  Link,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  useDisclosure,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import {
  FaEnvelope,
  FaLock,
  FaPhone,
  FaEye,
  FaEyeSlash,
  FaMusic,
  FaChurch,
  FaMapMarkedAlt,
  FaCalendarCheck,
  FaTasks,
  FaVideo,
  FaStar,
  FaSearch,
  FaEuroSign,
  FaFilter,
  FaCheckCircle,
  FaComments,
  FaLightbulb,
  FaFileAlt,
  FaClock,
  FaMapMarkerAlt,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaInfoCircle,
} from 'react-icons/fa';

const FORM_NOTICE = {
  warning: { bg: '#fff7ed', border: '#fdba74', icon: '#ea580c', title: '#9a3412', text: '#1f2937' },
  info: { bg: '#eff6ff', border: '#93c5fd', icon: '#2563eb', title: '#1e40af', text: '#1f2937' },
  error: { bg: '#fef2f2', border: '#fca5a5', icon: '#dc2626', title: '#991b1b', text: '#1f2937' },
}

const FormNotice = ({ status = 'warning', title, children, mt = 3 }) => {
  const tone = FORM_NOTICE[status] || FORM_NOTICE.warning
  const IconCmp = status === 'info' ? FaInfoCircle : FaExclamationCircle
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={3}
      mt={mt}
      py={2}
      px={3}
      rounded="lg"
      bg={tone.bg}
      borderWidth="1px"
      borderColor={tone.border}
      color={tone.text}
    >
      <Box as={IconCmp} boxSize={4} color={tone.icon} mt="2px" flexShrink={0} />
      <Box textAlign="left" minW={0}>
        {title ? (
          <Text fontSize="sm" fontWeight="600" color={tone.title}>
            {title}
          </Text>
        ) : null}
        {children ? (
          typeof children === 'string' ? (
            <Text fontSize="xs" color={tone.text} mt={title ? 1 : 0}>
              {children}
            </Text>
          ) : (
            <Box fontSize="xs" color={tone.text} mt={title ? 1 : 0}>
              {children}
            </Box>
          )
        ) : null}
      </Box>
    </Box>
  )
}

// Requisitos de contraseña segura
const passwordRequirements = [
  { id: 'length', label: 'Al menos 8 caracteres', test: (pwd) => pwd.length >= 8 },
  { id: 'uppercase', label: 'Una letra mayúscula', test: (pwd) => /[A-Z]/.test(pwd) },
  { id: 'lowercase', label: 'Una letra minúscula', test: (pwd) => /[a-z]/.test(pwd) },
  { id: 'number', label: 'Un número', test: (pwd) => /\d/.test(pwd) },
  { id: 'symbol', label: 'Un símbolo especial (!@#$%...)', test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(pwd) },
];

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, error: authError, clearError } = useAuth();
  const [formData, setFormData] = useState({
    userType: '', // 'banda' o 'cofradia'
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    nombre: '', // Nombre de la banda o cofradía
    tipoEntidadId: '', // Solo cofradía: catálogo tipos_entidad_cadencya
    tipoBandaId: '', // Solo banda: maestro tipos_agrupacion_instrumental (API instrumentos/tipos-agrupacion)
    acceptTerms: false,
  });
  const [tiposEntidad, setTiposEntidad] = useState([]);
  const [tiposEntidadLoading, setTiposEntidadLoading] = useState(false);
  const [tiposBanda, setTiposBanda] = useState([]);
  const [tiposBandaLoading, setTiposBandaLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal de confirmación de tipo de cuenta
  const { open: isConfirmModalOpen, onOpen: onConfirmModalOpen, onClose: onConfirmModalClose } = useDisclosure();

  // Limpiar error del AuthContext al montar el componente
  useEffect(() => {
    if (authError) {
      clearError();
    }
  }, []); // Solo al montar

  // Efecto para detectar el tipo de usuario desde la URL (solo al cargar inicialmente)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tipoParam = urlParams.get('tipo');
    
    if (tipoParam === 'banda' || tipoParam === 'cofradia') {
      // Solo actualizar si el userType actual está vacío o es diferente
      setFormData(prev => {
        if (prev.userType !== tipoParam) {
          return { ...prev, userType: tipoParam };
        }
        return prev;
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (formData.userType !== 'cofradia' && formData.userType !== 'banda') {
      setTiposEntidad([]);
      setTiposEntidadLoading(false);
      setTiposBanda([]);
      setTiposBandaLoading(false);
      return;
    }
    let cancelled = false;

    if (formData.userType === 'cofradia') {
      setTiposBanda([]);
      setTiposBandaLoading(false);
      setTiposEntidadLoading(true);
      (async () => {
        try {
          const res = await catalogoAPI.getTiposEntidadCadencya();
          if (!cancelled) {
            setTiposEntidad(Array.isArray(res.data) ? res.data : []);
          }
        } catch (e) {
          console.error('No se pudieron cargar tipos de entidad', e);
          if (!cancelled) setTiposEntidad([]);
        } finally {
          if (!cancelled) setTiposEntidadLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setTiposEntidad([]);
    setTiposEntidadLoading(false);
    setTiposBandaLoading(true);
    (async () => {
      try {
        const res = await instrumentosAPI.getTiposAgrupacion();
        if (!cancelled) {
          setTiposBanda(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) {
        console.error('No se pudieron cargar tipos de banda', e);
        if (!cancelled) setTiposBanda([]);
      } finally {
        if (!cancelled) setTiposBandaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.userType]);

  const validateForm = () => {
    const newErrors = {};

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'El email es obligatorio';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    // Validar contraseña con todos los requisitos de seguridad
    if (!formData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    } else {
      const failedRequirements = passwordRequirements.filter(req => !req.test(formData.password));
      if (failedRequirements.length > 0) {
        newErrors.password = 'La contraseña no cumple todos los requisitos de seguridad';
      }
    }

    // Validar confirmación de contraseña
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    // Validar teléfono (opcional pero si se introduce debe ser válido)
    if (formData.phone && !/^[+]?[0-9]{9,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'El teléfono no tiene un formato válido';
    }

    // Validar tipo de usuario
    if (!formData.userType) {
      newErrors.userType = 'Debes seleccionar si eres una banda o cofradía';
    }

    if (formData.userType === 'cofradia') {
      if (tiposEntidadLoading) {
        newErrors.tipoEntidadId = 'Espera a que cargue el listado de tipos de entidad';
      } else if (!tiposEntidad.length) {
        newErrors.tipoEntidadId =
          'No se pudieron cargar los tipos de entidad. Recarga la página o inténtalo más tarde.';
      } else if (!formData.tipoEntidadId) {
        newErrors.tipoEntidadId = 'Selecciona el tipo de entidad';
      }
    }

    if (formData.userType === 'banda') {
      if (tiposBandaLoading) {
        newErrors.tipoBandaId = 'Espera a que cargue el listado de tipos de banda';
      } else if (!tiposBanda.length) {
        newErrors.tipoBandaId =
          'No se pudieron cargar los tipos de banda. Recarga la página o inténtalo más tarde.';
      } else if (!formData.tipoBandaId) {
        newErrors.tipoBandaId = 'Selecciona el tipo de banda';
      }
    }

    // Validar términos y condiciones
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'userType') {
        if (value !== 'cofradia') next.tipoEntidadId = '';
        if (value !== 'banda') next.tipoBandaId = '';
      }
      return next;
    });
    
    // Si se cambia el tipo de usuario, actualizar la URL sin recargar el componente
    if (field === 'userType') {
      if (value) {
        // Si selecciona un tipo, añadir el query parameter usando replace para no perder el estado
        navigate(`/registro?tipo=${value}`, { replace: true });
      } else {
        // Si deselecciona (cambiar tipo), volver a /registro sin params
        navigate('/registro', { replace: true });
      }
    }
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e) => {
    console.log('📝 handleSubmit ejecutado', e);
    e.preventDefault();
    console.log('✋ preventDefault ejecutado');
    
    if (!validateForm()) {
      console.log('❌ Validación falló');
      return;
    }

    console.log('✅ Validación exitosa');
    // Limpiar errores previos antes de abrir el modal
    setErrors({});
    
    // Abrir modal de confirmación
    console.log('🔓 Abriendo modal de confirmación');
    onConfirmModalOpen();
  };

  const handleConfirmedSubmit = async () => {
    console.log('🚀 Iniciando registro...');
    onConfirmModalClose();
    setIsLoading(true);
    
    try {
      // Preparar datos para la API
      const nombreTrim = formData.nombre?.trim();
      const tipoSel =
        formData.userType === 'cofradia'
          ? tiposEntidad.find((t) => t.id === formData.tipoEntidadId)
          : null;
      const nombreDefaultCofradia = tipoSel
        ? getMiEntidadNombreDefecto(tipoSel)
        : 'Mi entidad';

      const registerData = {
        email: formData.email,
        password: formData.password,
        userType: formData.userType.toUpperCase(), // BANDA o COFRADIA
        nombre:
          nombreTrim ||
          (formData.userType === 'banda' ? 'Mi Banda' : nombreDefaultCofradia),
        personaResponsable: {
          nombre: '',
          apellidos: '',
          email: formData.email,
          telefono: formData.phone || '',
        },
        localizacion: {
          ciudad: '',
          provincia: '',
        },
        ...(formData.userType === 'cofradia' && formData.tipoEntidadId
          ? { tipoEntidadId: formData.tipoEntidadId }
          : {}),
        ...(formData.userType === 'banda' && formData.tipoBandaId
          ? { tipoBandaId: formData.tipoBandaId }
          : {}),
      };
      
      console.log('📤 Enviando datos:', registerData);
      const result = await register(registerData);
      console.log('📥 Respuesta del servidor:', result);
      
      if (result.success) {
        console.log('✅ Registro exitoso, redirigiendo...');
        trackSignUp('email', formData.userType.toUpperCase());
        // Redirigir al perfil correspondiente según el tipo de usuario
        if (formData.userType === 'banda') {
          navigate('/banda/perfil');
        } else if (formData.userType === 'cofradia') {
          navigate('/cofradia/perfil');
        }
      } else {
        console.log('❌ Error en el registro:', result.error);
        // Mostrar el error específico del backend
        setErrors({ submit: result.error });
        setIsLoading(false);
        // Hacer scroll hacia arriba para mostrar el error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
    } catch (error) {
      console.error('💥 Excepción en el registro:', error);
      // Intentar extraer el mensaje de error del servidor
      const errorMessage = error.response?.data?.message || 'Error al conectar con el servidor. Por favor, inténtalo de nuevo.';
      console.log('📝 Mensaje de error a mostrar:', errorMessage);
      setErrors({ submit: errorMessage });
      setIsLoading(false);
      // Hacer scroll hacia arriba para mostrar el error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-br, purple.50, white, gold.50)">
      <SEO
        title="Registro"
        description="Regístrate en Cadencya como banda de música o cofradía. Crea tu perfil y empieza a conectar."
        path="/registro"
      />
      {/* Header simple */}
      <Box bg="white" boxShadow="sm" py={5}>
        <Container maxW="7xl">
          <HStack justify="space-between">
            <HStack 
              gap={3}
              cursor="pointer"
              onClick={() => navigate('/')}
            >
              <Image 
                src="/logo y texto cadencya.png" 
                alt="Cadencya" 
                h={{ base: "55px", md: "70px" }}
                w="auto"
              />
            </HStack>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
              fontWeight="600"
            >
              Iniciar sesión
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Container maxW="6xl" py={{ base: 8, md: 16 }}>
        <VStack gap={{ base: 8, md: 12 }}>
          {/* Título */}
          <VStack gap={3} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight="800"
              color="gray.800"
            >
              Únete a Cadencya
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600">
              {formData.userType === 'banda' 
                ? 'Conecta con cofradías de toda España'
                : formData.userType === 'cofradia'
                ? 'Encuentra la banda perfecta para tus eventos'
                : 'Selecciona cómo quieres registrarte'
              }
            </Text>
          </VStack>

          {/* Selección de tipo de usuario */}
          {!formData.userType ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full">
              <Box
                bg="white"
                p={8}
                rounded="3xl"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-8px)',
                  boxShadow: '2xl',
                }}
                boxShadow="lg"
                onClick={() => { handleInputChange('userType', 'banda'); trackUserTypeSelection('BANDA'); }}
                position="relative"
                overflow="hidden"
              >
                <VStack gap={6}>
                  <Box h={{ base: "220px", md: "250px" }} display="flex" alignItems="center" justifyContent="center">
                    <Image
                      src="/musico-cofradia-1.png"
                      alt="Banda"
                      maxH={{ base: "220px", md: "250px" }}
                      w="auto"
                      filter="drop-shadow(0 10px 20px rgba(0,0,0,0.1))"
                    />
                  </Box>
                  <VStack gap={2}>
                    <Heading size="lg" color="purple.600">
                      Soy una Banda
                    </Heading>
                    <Text color="gray.600" textAlign="center">
                      Ofrece tus servicios musicales a cofradías y entidades
                    </Text>
                  </VStack>
                  <Button
                    bg="purple.600"
                    color="white"
                    rounded="full"
                    size="lg"
                    w="full"
                    _hover={{ bg: 'purple.700' }}
                  >
                    Continuar
                  </Button>
                </VStack>
              </Box>

              <Box
                bg="white"
                p={8}
                rounded="3xl"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-8px)',
                  boxShadow: '2xl',
                }}
                boxShadow="lg"
                onClick={() => { handleInputChange('userType', 'cofradia'); trackUserTypeSelection('COFRADIA'); }}
                position="relative"
                overflow="hidden"
              >
                <VStack gap={6}>
                  <Box h={{ base: "220px", md: "250px" }} display="flex" alignItems="center" justifyContent="center">
                    <Image
                      src="/capirote-morado.png"
                      alt="Cofradía"
                      maxH={{ base: "220px", md: "250px" }}
                      w="auto"
                      filter="drop-shadow(0 10px 20px rgba(0,0,0,0.1))"
                    />
                  </Box>
                  <VStack gap={2}>
                    <Heading size="lg" color="blue.600">
                      Soy una Cofradía o Entidad
                    </Heading>
                    <Text color="gray.600" textAlign="center">
                      Encuentra bandas para tus eventos religiosos
                    </Text>
                  </VStack>
                  <Button
                    bg="blue.600"
                    color="white"
                    rounded="full"
                    size="lg"
                    w="full"
                    _hover={{ bg: 'blue.700' }}
                  >
                    Continuar
                  </Button>
                </VStack>
              </Box>
            </SimpleGrid>
          ) : (
            // Layout de dos columnas: Info + Formulario
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} w="full">
              {/* Columna Izquierda - Información */}
              <Box
                bg={formData.userType === 'banda' ? 'purple.600' : 'blue.600'}
                p={{ base: 8, md: 12 }}
                rounded="3xl"
                color="white"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <VStack gap={8} align="start">
                  <Image
                    src={formData.userType === 'banda' ? '/musico fondo andalucia.png' : '/capirote registro.png'}
                    alt="Mascota"
                    maxW={{ base: "100%", md: "500px" }}
                    ml={{ base: "0", md: "-40px" }}
                    filter="drop-shadow(0 20px 40px rgba(0,0,0,0.3))"
                  />
                  
                  <VStack gap={4} align="start" w="full">
                    <Heading size="xl" color="white">
                      {formData.userType === 'banda' 
                        ? '¿Por qué registrar tu banda?' 
                        : '¿Por qué registrar tu cofradía o entidad?'}
                    </Heading>
                    
                    {formData.userType === 'banda' ? (
                      <VStack gap={3} align="start" w="full">
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Amplía tu cartera de actuaciones</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Diferénciate y da a conocer tu estilo</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Muestra tu repertorio y experiencia</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Aumenta tu reputación</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Gestión integral desde la plataforma</Text>
                        </HStack>
                      </VStack>
                    ) : (
                      <VStack gap={3} align="start" w="full">
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Escoge la música ideal para tus cultos</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Perfiles detallados de cada banda</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Lee valoraciones verificadas</Text>
                        </HStack>
                        <HStack gap={3}>
                          <Box w={2} h={2} bg="gold.300" rounded="full" flexShrink={0} />
                          <Text>Gestión integral desde la plataforma</Text>
                        </HStack>
                      </VStack>
                    )}
                  </VStack>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleInputChange('userType', '')}
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  >
                    ← Cambiar tipo de usuario
                  </Button>
                </VStack>
              </Box>

              {/* Columna Derecha - Formulario */}
              <Box
                bg="white"
                p={{ base: 6, md: 10 }}
                rounded="3xl"
                boxShadow="xl"
              >
                <VStack gap={6} mb={6}>
                  <Heading size="lg">
                    {formData.userType === 'banda' ? 'Registra tu banda' : 'Registra tu cofradía o entidad'}
                  </Heading>
                  <Text color="gray.600" textAlign="center">
                    Completa el formulario para empezar
                  </Text>
                </VStack>

              <form onSubmit={handleSubmit}>
                <VStack gap={5}>
                  {/* Error general */}
                  {errors.submit && (
                    <FormNotice status="error" mt={0} title={errors.submit}>
                      {errors.submit.toLowerCase().includes('email ya está registrado') ? (
                        <>
                          ¿Ya tienes una cuenta?{' '}
                          <Button
                            variant="plain"
                            colorPalette="purple"
                            size="sm"
                            onClick={() => navigate('/login')}
                          >
                            Inicia sesión aquí
                          </Button>
                        </>
                      ) : null}
                    </FormNotice>
                  )}

                  {/* Email */}
                  <Field.Root invalid={!!errors.email}>
                    <Field.Label fontWeight="600">Email</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaEnvelope} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                    </InputGroup>
                    <Field.ErrorText>{errors.email}</Field.ErrorText>
                  </Field.Root>

                  {/* Contraseña */}
                  <Field.Root invalid={!!errors.password}>
                    <Field.Label fontWeight="600">Contraseña</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaLock} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Contraseña segura"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Icon as={showPassword ? FaEyeSlash : FaEye} /></IconButton>
                      </InputRightElement>
                    </InputGroup>
                    <Field.ErrorText>{errors.password}</Field.ErrorText>
                  </Field.Root>
                  
                  {/* Indicadores visuales de requisitos de contraseña - siempre visibles para guiar al usuario */}
                  <Box p={3} bg="gray.50" rounded="lg" border="1px solid" borderColor="gray.200">
                    <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
                      Tu contraseña debe tener:
                    </Text>
                    <SimpleGrid columns={1} gap={1}>
                      {passwordRequirements.map((req) => {
                        const isValid = formData.password ? req.test(formData.password) : false;
                        return (
                          <HStack key={req.id} gap={2}>
                            <Icon
                              as={isValid ? FaCheck : FaTimes}
                              color={isValid ? 'green.500' : 'gray.400'}
                              boxSize={3}
                            />
                            <Text
                              fontSize="xs"
                              color={isValid ? 'green.600' : 'gray.500'}
                              fontWeight={isValid ? '500' : '400'}
                            >
                              {req.label}
                            </Text>
                          </HStack>
                        );
                      })}
                    </SimpleGrid>
                  </Box>

                  {/* Confirmar Contraseña */}
                  <Field.Root invalid={!!errors.confirmPassword}>
                    <Field.Label fontWeight="600">Confirmar contraseña</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaLock} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Repite tu contraseña"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                      <InputRightElement>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Icon as={showConfirmPassword ? FaEyeSlash : FaEye} /></IconButton>
                      </InputRightElement>
                    </InputGroup>
                    <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>
                  </Field.Root>

                  {/* Teléfono */}
                  <Field.Root invalid={!!errors.phone}>
                    <Field.Label fontWeight="600">Teléfono (opcional)</Field.Label>
                    <InputGroup size="lg">
                      <InputLeftElement>
                        <Icon as={FaPhone} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="tel"
                        placeholder="+34 600 123 456"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        rounded="xl"
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{ borderColor: 'purple.500', bg: 'white' }}
                      />
                    </InputGroup>
                    <Field.ErrorText>{errors.phone}</Field.ErrorText>
                  </Field.Root>

                  {formData.userType === 'banda' && (
                    <Field.Root invalid={!!errors.tipoBandaId}>
                      <Field.Label fontWeight="600">Tipo de banda</Field.Label>
                      {tiposBandaLoading ? (
                        <HStack gap={2}>
                          <Spinner size="sm" color="purple.500" />
                          <Text fontSize="sm" color="gray.600">
                            Cargando tipos…
                          </Text>
                        </HStack>
                      ) : (
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="lg"
                            rounded="xl"
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: 'gray.300' }}
                            _focus={{ borderColor: 'purple.500', bg: 'white' }}
                            placeholder="Selecciona el tipo"
                            value={formData.tipoBandaId || ''}
                            onChange={(e) => handleInputChange('tipoBandaId', e.target.value)}>
                            <option value="" disabled>
                              Selecciona el tipo
                            </option>
                            {tiposBanda.map((tipo) => (
                              <option key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      )}
                      <Field.HelperText color="gray.600">
                        Define cómo se clasifica tu agrupación en búsquedas y filtros. No podrás cambiarlo después del
                        registro.
                      </Field.HelperText>
                      <FormNotice status="warning" title="Tipo de banda definitivo">
                        Una vez creada la cuenta, el tipo de banda no se puede modificar desde el perfil. Elige el
                        que mejor encaje con tu formación.
                      </FormNotice>
                      <Field.ErrorText>{errors.tipoBandaId}</Field.ErrorText>
                    </Field.Root>
                  )}

                  {formData.userType === 'cofradia' && (
                    <Field.Root invalid={!!errors.tipoEntidadId}>
                      <Field.Label fontWeight="600">Tipo de entidad</Field.Label>
                      {tiposEntidadLoading ? (
                        <HStack gap={2}>
                          <Spinner size="sm" color="blue.500" />
                          <Text fontSize="sm" color="gray.600">
                            Cargando tipos…
                          </Text>
                        </HStack>
                      ) : (
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="lg"
                            rounded="xl"
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: 'gray.300' }}
                            _focus={{ borderColor: 'blue.500', bg: 'white' }}
                            placeholder="Selecciona el tipo"
                            value={formData.tipoEntidadId || ''}
                            onChange={(e) => handleInputChange('tipoEntidadId', e.target.value)}>
                            <option value="" disabled>
                              Selecciona el tipo
                            </option>
                            {tiposEntidad.map((tipo) => (
                              <option key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      )}
                      <Field.HelperText color="gray.600">
                        Define qué apartados de perfil verás (cofradía, ayuntamiento, asociación cultural, etc.).
                        No podrás cambiarlo después del registro.
                      </Field.HelperText>
                      <FormNotice status="warning" title="Tipo de entidad definitivo">
                        Una vez creada la cuenta, el tipo de entidad no se puede modificar desde el perfil.
                        Elige el que mejor encaje con tu organización.
                      </FormNotice>
                      <Field.ErrorText>{errors.tipoEntidadId}</Field.ErrorText>
                    </Field.Root>
                  )}

                  {/* Términos y Condiciones */}
                  <Field.Root invalid={!!errors.acceptTerms}>
                    <Checkbox.Root
                      onCheckedChange={(details) => handleInputChange('acceptTerms', details.checked === true)}
                      colorPalette="purple"
                      size="lg"
                      checked={formData.acceptTerms}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" color="gray.600">
                        Acepto los{' '}
                        <Link as={RouterLink} to="/terminos-de-uso" target="_blank" color="purple.600" fontWeight="600" textDecoration="underline">
                          términos y condiciones
                        </Link>
                        {' '}y he leído la{' '}
                        <Link as={RouterLink} to="/politica-privacidad" target="_blank" color="purple.600" fontWeight="600" textDecoration="underline">
                          política de privacidad
                        </Link>
                      </Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Field.ErrorText>{errors.acceptTerms}</Field.ErrorText>
                  </Field.Root>

                  {/* Botón de registro */}
                  <Button
                    type="submit"
                    bg={formData.userType === 'banda' ? 'purple.600' : 'green.600'}
                    color="white"
                    size="lg"
                    w="full"
                    h="14"
                    rounded="full"
                    loading={isLoading}
                    loadingText="Creando cuenta..."
                    fontSize="lg"
                    fontWeight="700"
                    _hover={{ 
                      bg: formData.userType === 'banda' ? 'purple.700' : 'green.700',
                      transform: 'scale(1.02)',
                    }}
                    _active={{ transform: 'scale(0.98)' }}
                  >
                    Crear cuenta gratis
                  </Button>

                  {/* Link a login */}
                  <Text fontSize="sm" color="gray.600" textAlign="center" pt={2}>
                    ¿Ya tienes una cuenta?{' '}
                    <Link 
                      as={RouterLink}
                      to="/login"
                      color="purple.600" 
                      fontWeight="600"
                      _hover={{ textDecoration: "underline" }}
                    >
                      Iniciar sesión
                    </Link>
                  </Text>
                </VStack>
              </form>
              </Box>
            </SimpleGrid>
          )}

          {/* Sección de detalles expandidos - Solo visible cuando hay userType seleccionado */}
          {formData.userType && (
            <Box w="full" mt={12}>
              <VStack gap={8} align="start" w="full">
                <Heading 
                  size="xl" 
                  textAlign="center" 
                  w="full"
                  color="gray.800"
                >
                  {formData.userType === 'banda' 
                    ? 'Todo lo que obtienes al registrar tu banda' 
                    : 'Todo lo que obtienes al registrar tu cofradía'}
                </Heading>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full">
                  {formData.userType === 'banda' ? (
                    <>
                      {/* SERVICIOS */}
                      {/* Servicio 1 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="purple.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="purple.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaCalendarCheck} w={8} h={8} color="purple.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="purple.600">
                              Amplía tu cartera de actuaciones
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Las cofradías y hermandades buscan activamente bandas que aporten variedad y calidad a sus cultos públicos, tanto internos como externos. Con Cadencya, obtendrás visibilidad para acceder a nuevas oportunidades y ampliar tu calendario de actuaciones.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 2 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="purple.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="purple.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaLightbulb} w={8} h={8} color="purple.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="purple.600">
                              Diferénciate
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Encuentra un espacio en el que pongas en valor tu estilo e historia. A través de la plataforma, podrás dar a conocer tu propia idiosincrasia, lo que te ayudará a conectar con las entidades interesadas.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 3 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="purple.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="purple.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaVideo} w={8} h={8} color="purple.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="purple.600">
                              Muestra tu repertorio y experiencia
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Crea un perfil profesional completo en el que recojas los detalles más relevantes para tu formación: el estilo, tu repertorio musical, tu trayectoria artística y cualquier contenido multimedia de tus actuaciones pasadas. Las cofradías podrán conocer tu trabajo antes de contactarte, aumentando las posibilidades de derivar en una contratación.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 4 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="purple.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="purple.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaStar} w={8} h={8} color="purple.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="purple.600">
                              Aumenta tu reputación
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Recibe valoraciones de las cofradías y hermandades con las que trabajas. Las reseñas aumentarán tu credibilidad y ofrecerán confianza a la hora de establecer nuevos contratos.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 5 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="purple.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="purple.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaTasks} w={8} h={8} color="purple.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="purple.600">
                              Gestión integral desde la plataforma
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Centraliza la gestión de tus solicitudes, el calendario de disponibilidad, la comunicación con las entidades interesadas y el seguimiento de los contratos. Todo en un solo lugar, ahorrándote tiempo y facilitando la organización.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* FUNCIONALIDADES */}
                      {/* Funcionalidad 1 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaMapMarkerAlt} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Filtrado geográfico
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Tú pones los límites de desplazamiento. Descubre los eventos que tienen lugar en tu entorno y encuentra nuevas oportunidades más allá de tus fronteras. A través del buscador de Cadencya, podrás determinar tu alcance geográfico para conectar con entidades contratantes en otras ciudades y comarcas.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Funcionalidad 2 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaClock} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Calendario
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Podrás dar a conocer tu disponibilidad para que las entidades interesadas conozcan las fechas libres en tu calendario.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Funcionalidad 3 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaComments} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Comunicación directa
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Utiliza un canal de comunicación estructurado para conocer a las entidades interesadas.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* SERVICIOS */}
                      {/* Servicio 1 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="blue.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="blue.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaMusic} w={8} h={8} color="blue.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="blue.600">
                              Escoge la música ideal para tus cultos
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Accede a un directorio completo de formaciones musicales verificadas y disponibles (bandas de palio o cristo, grupos de cámara, coros, saeteros...). Encuentra la banda sonora adecuada a tu idiosincrasia.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 2 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="blue.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="blue.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaFileAlt} w={8} h={8} color="blue.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="blue.600">
                              Perfiles detallados
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Consulta la información completa de cada banda: repertorio, experiencia, fotos y videos de sus actuaciones.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 3 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="blue.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="blue.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaCheckCircle} w={8} h={8} color="blue.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="blue.600">
                              Lee valoraciones verificadas
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Contrata con confianza basándote en experiencias reales. Consulta las opiniones y valoraciones de otras cofradías con las que ya han colaborado las formaciones musicales. Conoce su puntualidad, profesionalidad, calidad musical y trato.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Servicio 4 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="blue.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="blue.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaTasks} w={8} h={8} color="blue.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="blue.600">
                              Gestión integral desde la plataforma
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Centraliza la gestión de tus solicitudes, la comunicación con las entidades musicales y el seguimiento de los contratos. Todo en un solo lugar, ahorrándote tiempo y facilitando la organización.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* FUNCIONALIDADES */}
                      {/* Funcionalidad 1 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaMapMarkerAlt} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Filtrado geográfico
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Localiza entidades musicales cerca de tu ubicación o descubre nuevas oportunidades en otras localidades.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Funcionalidad 2 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaClock} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Disponibilidad en tiempo real
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Consulta en tiempo real la disponibilidad de cada banda. Filtra los resultados de la búsqueda en función de las fechas específicas para Semana Santa u otros cultos religiosos.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>

                      {/* Funcionalidad 3 */}
                      <Box 
                        bg="white" 
                        p={8} 
                        rounded="2xl" 
                        boxShadow="lg"
                        borderTop="4px solid"
                        borderColor="gold.500"
                        transition="all 0.3s"
                        _hover={{ boxShadow: 'xl', transform: 'translateY(-2px)' }}
                      >
                        <HStack gap={6} align="start">
                          <Box
                            bg="gold.100"
                            p={4}
                            rounded="xl"
                            flexShrink={0}
                          >
                            <Icon as={FaComments} w={8} h={8} color="gold.600" />
                          </Box>
                          <VStack gap={3} align="start" flex={1}>
                            <Heading size="md" color="gold.600">
                              Comunicación directa
                            </Heading>
                            <Text color="gray.600" lineHeight="tall">
                              Accede a un canal de comunicación estructurado para comunicarte con las entidades musicales interesadas. Negocia términos contractuales, acuerda repertorios y resuelve dudas sin intermediarios.
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    </>
                  )}
                </SimpleGrid>
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>

      {/* Modal de confirmación de tipo de cuenta */}
      <Dialog.Root open={isConfirmModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onConfirmModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header textAlign="center" pb={2}>
                <Icon 
                  as={formData.userType === 'banda' ? FaMusic : FaChurch} 
                  boxSize={10} 
                  color="purple.500" 
                  mb={2}
                />
                <Text>Confirmar tipo de cuenta</Text>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body textAlign="center" py={4}>
                <Text fontSize="lg" fontWeight="medium" mb={2}>
                  ¿Seguro que eres {formData.userType === 'banda' ? 'una banda' : 'una cofradía o entidad'}?
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {formData.userType === 'banda' 
                    ? 'Si eres una cofradía o entidad buscando bandas, debes registrarte como cofradía o entidad.'
                    : 'Si eres una banda de música buscando eventos, debes registrarte como banda.'}
                </Text>
                <FormNotice status="info" mt={4}>
                  Una vez creada la cuenta, no podrás cambiar el tipo de usuario.
                </FormNotice>
                {formData.userType === 'banda' && (
                  <FormNotice status="warning">
                    El tipo de banda que has elegido quedará fijado y no podrás cambiarlo después. Comprueba que sea el
                    correcto antes de confirmar.
                  </FormNotice>
                )}
                {formData.userType === 'cofradia' && (
                  <FormNotice status="warning">
                    El tipo de entidad que has elegido (hermandad, ayuntamiento, fundación, etc.) quedará fijado y no
                    podrás cambiarlo después. Comprueba que sea el correcto antes de confirmar.
                  </FormNotice>
                )}
              </Dialog.Body>
              <Dialog.Footer justifyContent="center" gap={3} pt={2}>
                <Button type="button" variant="outline" onClick={onConfirmModalClose}><Icon as={FaTimes} />Volver
                              </Button>
                <Button
                  type="button"
                  colorPalette="purple"
                  onClick={handleConfirmedSubmit}
                  loading={isLoading}><Icon as={FaCheck} />Sí, confirmar
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default Register;
